import { useMemo } from 'react'

import { findMostRecentInProgressIndex, isPast } from 'components/meetings/utils'
import { useCurrentTime } from 'shared/contexts'

import { useMeetingsContext } from '../contexts/MeetingsContext'

/**
 * Represents a meeting event with time and status information.
 */
interface Meeting {
  /** ISO 8601 datetime string for when the meeting starts */
  startTime?: string
  /** ISO 8601 datetime string for when the meeting ends */
  endTime?: string
  /** Whether the meeting has already occurred */
  past?: boolean
  [key: string]: unknown
}

/**
 * Return value from the useTimeIndicator hook.
 */
interface UseTimeIndicatorReturn {
  /** Index where the time indicator should be displayed in the meetings array */
  indicatorIndex: number
  /** Index of the most recently started in-progress meeting, or -1 if none */
  mostRecentInProgressIndex: number
  /** Whether to show the indicator after all meetings in the list */
  showIndicatorAfterAll: boolean
  /** Whether realtime features are enabled for more accurate time calculations */
  hasRealtimeFeatures: boolean
}

/**
 * Custom hook to calculate where to position the current time indicator
 * within a list of meetings for a given date.
 *
 * Logic:
 * 1. If there's an in-progress meeting, show indicator before the most recent one
 * 2. Otherwise, show before the first non-past event
 * 3. If all events are past, show after all events
 *
 * @param rawDate - Date string in YYYY-MM-DD format for the meeting group
 * @param events - Array of meetings for this date
 * @returns {UseTimeIndicatorReturn} Object with indicatorIndex, mostRecentInProgressIndex, showIndicatorAfterAll, and hasRealtimeFeatures
 */
export const useTimeIndicator = (
  rawDate: string,
  events: Meeting[]
): UseTimeIndicatorReturn => {
  const { hasRealtimeFeatures } = useMeetingsContext() ?? {}
  const currentTime = useCurrentTime()

  // Don't memoize - this is a cheap comparison and we want it to recalculate
  // when currentTime updates (including when the date changes at midnight)
  const isToday = rawDate === currentTime.format('YYYY-MM-DD')

  // Find the most recent in-progress meeting (latest start time, or furthest down in list if tied)
  const mostRecentInProgressIndex = useMemo(
    () => (isToday ? findMostRecentInProgressIndex(events, currentTime) : -1),
    [isToday, events, currentTime]
  )

  // Determine indicator position:
  // 1. Before the most recent in-progress meeting (if any)
  // 2. Before the first non-past event (if no in-progress)
  // 3. After all events (if all are past)
  const indicatorIndex = useMemo(() => {
    if (!isToday || events.length === 0) return -1

    if (mostRecentInProgressIndex !== -1) {
      return mostRecentInProgressIndex
    }

    const isMeetingInPast = (event: Meeting) => (hasRealtimeFeatures
      ? isPast(event, currentTime)
      : event.past)

    const firstNotPastIndex = events.findIndex((event) => !isMeetingInPast(event))
    return firstNotPastIndex !== -1 ? firstNotPastIndex : events.length
  }, [isToday, events, mostRecentInProgressIndex, hasRealtimeFeatures, currentTime])

  const showIndicatorAfterAll = indicatorIndex === events.length

  return {
    indicatorIndex,
    mostRecentInProgressIndex,
    showIndicatorAfterAll,
    hasRealtimeFeatures: hasRealtimeFeatures ?? false
  }
}
